// ADR 15.9.26ag §5 — `Disclosure`'s Escape handler, in a file of its own.
//
// Extracted for room, not for sharing: `Disclosure.tsx` stood at 299 of 300
// lines and needed one more edit. ADR 3.10.26f §3's header box of doors
// (`layout/switcher/SwitcherBox.tsx`) is its second caller.

import { useEffect, type RefObject } from 'react'

/**
 * `Escape` closes and returns focus to the trigger.
 *
 * A **deliberate extension** — Escape is a menu/dialog behaviour, not part of
 * the APG disclosure pattern — kept because 8.9.26i §3's account menu wants it
 * and one key map across both is worth more than pattern purity.
 *
 * Attached as a native listener rather than as an `onKeyDown` prop: the element
 * that has to hear it is the wrapper, and a `<div onKeyDown>` is a non-native
 * interactive element that `jsx-a11y` rejects on sight (8.9.26i AC10). A
 * listener on a ref carries no such claim — the interactive element is still
 * the button.
 */
export function useEscapeCloses(
  wrapper: RefObject<HTMLDivElement>,
  close: () => void,
  trigger: RefObject<HTMLButtonElement>,
): void {
  useEffect(() => {
    const node = wrapper.current
    if (node === null) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      close()
      trigger.current?.focus()
    }
    node.addEventListener('keydown', onKeyDown)
    return () => node.removeEventListener('keydown', onKeyDown)
  }, [wrapper, close, trigger])
}
